import React from "react";
import { useTranslation } from "react-i18next";
import { Contract } from "../../features/contracts/models/Contract";
import { ContractAcceptanceStatus } from "../../features/contracts/models/ContractAcceptanceStatus";

/**
 * Where the contract stands in the online-acceptance flow: green once accepted
 * (tooltip carries when and by whom), neutral while pending, hidden when never
 * requested — most contracts never enter the flow, so no chip means no noise.
 */
const ContractAcceptanceChip: React.FC<{ contract: Contract }> = ({ contract }) => {
  const { t, i18n } = useTranslation();

  if (contract.acceptanceStatus === ContractAcceptanceStatus.NotRequested) {
    return null;
  }

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" })
      .format(new Date(iso));

  if (contract.acceptanceStatus === ContractAcceptanceStatus.Accepted && contract.acceptedAt) {
    const label = contract.acceptedByGuardian
      ? t("billing.acceptance.acceptedByGuardian", "Accepted {{date}} by guardian", {
          date: formatDate(contract.acceptedAt),
        })
      : t("billing.acceptance.accepted", "Accepted {{date}}", { date: formatDate(contract.acceptedAt) });
    return (
      <span className="inline-block rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
        {label}
      </span>
    );
  }

  return (
    <span
      className="inline-block rounded-full bg-secondary-95 px-2.5 py-0.5 text-xs font-medium text-secondary-30 dark:bg-secondary-20 dark:text-secondary-80"
      title={
        contract.acceptanceRequestedAt
          ? t("billing.acceptance.pendingSince", "Requested {{date}}", {
              date: formatDate(contract.acceptanceRequestedAt),
            })
          : undefined
      }
    >
      {t("billing.acceptance.pending", "Awaiting acceptance")}
    </span>
  );
};

export default ContractAcceptanceChip;
